// useHqRealtime — subscribes to Supabase realtime channels for the
// tables the HQ cares about (leads / appointments / audit_events) and
// re-emits our typed hq events, so cross-USER changes (another agent
// updates a lead in another tab) flow into the same Home Attention
// Queue + preview refresh path as local mutations.
//
// Mounted once at app root by CommissionTracker.

import React from 'react';
import { supabase } from '../lib/supabase';
import { emitHqEvent, HQ_EVENTS } from './useHqEvents';

export function useHqRealtime(currentUser) {
  const ownerId = currentUser?.id;
  React.useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;
    const channels = [];

    const safeSub = (name, table, filter, onEvent) => {
      try {
        const ch = supabase
          .channel(`hq:${name}:${ownerId}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table,
            ...(filter ? { filter } : {}),
          }, (payload) => { if (!cancelled) { try { onEvent(payload); } catch {} } })
          .subscribe();
        channels.push(ch);
      } catch { /* realtime may be disabled on this instance */ }
    };

    safeSub('leads', 'leads', `owner_id=eq.${ownerId}`, (p) => {
      const stage = p?.new?.status || p?.old?.status;
      emitHqEvent(HQ_EVENTS.LEAD_STAGE_CHANGE, {
        leadId: p?.new?.id || p?.old?.id,
        stage,
        source: 'realtime',
      });
    });

    safeSub('appointments', 'appointments', null, (p) => {
      emitHqEvent(HQ_EVENTS.APPOINTMENT_UPDATED, {
        apptId: p?.new?.id || p?.old?.id,
        status: p?.new?.status,
        source: 'realtime',
      });
    });

    safeSub('audit', 'audit_events', `owner_id=eq.${ownerId}`, (p) => {
      emitHqEvent(HQ_EVENTS.AUDIT_EVENT, {
        category: p?.new?.category,
        source: 'realtime',
      });
    });

    return () => {
      cancelled = true;
      for (const ch of channels) { try { supabase.removeChannel(ch); } catch {} }
    };
  }, [ownerId]);
}
